import {
  FC,
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Popover } from '@headlessui/react';

const VIEWPORT_MARGIN_PX = 8;

type FlyoutCoords = {
  top: number;
  left: number;
  maxHeight: number;
};

type SidebarFlyoutProps = {
  buttonContent: ReactNode;
  buttonClassName?: string;
  buttonTestId?: string;
  buttonAriaLabel?: string;
  panelTestId?: string;
  children: ReactNode;
  onOpenChange?: (open: boolean) => void;
};

const SidebarFlyout: FC<SidebarFlyoutProps> = ({
  buttonContent,
  buttonClassName,
  buttonTestId,
  buttonAriaLabel,
  panelTestId,
  children,
  onOpenChange
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<FlyoutCoords>({
    top: 0,
    left: 0,
    maxHeight: 0
  });

  return (
    <Popover className="w-full">
      {({ open, close }) => (
        <SidebarFlyoutBody
          open={open}
          close={close}
          buttonRef={buttonRef}
          coords={coords}
          setCoords={setCoords}
          buttonContent={buttonContent}
          buttonClassName={buttonClassName}
          buttonTestId={buttonTestId}
          buttonAriaLabel={buttonAriaLabel}
          panelTestId={panelTestId}
          onOpenChange={onOpenChange}
        >
          {children}
        </SidebarFlyoutBody>
      )}
    </Popover>
  );
};

type SidebarFlyoutBodyProps = SidebarFlyoutProps & {
  open: boolean;
  close: () => void;
  buttonRef: RefObject<HTMLButtonElement>;
  coords: FlyoutCoords;
  setCoords: (coords: FlyoutCoords) => void;
};

const SidebarFlyoutBody: FC<SidebarFlyoutBodyProps> = ({
  open,
  close,
  buttonRef,
  coords,
  setCoords,
  buttonContent,
  buttonClassName,
  buttonTestId,
  buttonAriaLabel,
  panelTestId,
  children,
  onOpenChange
}) => {
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const updatePosition = () => {
      if (!buttonRef.current) {
        return;
      }
      const rect = buttonRef.current.getBoundingClientRect();
      const maxTop = Math.max(VIEWPORT_MARGIN_PX, window.innerHeight - 280);
      const top = Math.min(rect.top, maxTop);
      setCoords({
        top,
        left: rect.right + VIEWPORT_MARGIN_PX,
        maxHeight: window.innerHeight - top - VIEWPORT_MARGIN_PX
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [open, buttonRef, setCoords]);

  return (
    <>
      <Popover.Button
        ref={buttonRef}
        className={buttonClassName}
        data-testid={buttonTestId}
        aria-label={buttonAriaLabel}
      >
        {buttonContent}
      </Popover.Button>
      {open &&
        createPortal(
          <Popover.Panel
            static
            data-testid={panelTestId}
            className="hover-scrollbar fixed z-50 w-56 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
            style={{
              top: coords.top,
              left: coords.left,
              maxHeight: coords.maxHeight
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                return;
              }
              close();
            }}
          >
            {children}
          </Popover.Panel>,
          document.body
        )}
    </>
  );
};

export default SidebarFlyout;
